import React, { useState } from 'react'
import styled from 'styled-components'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { ChevronLeft } from '@mui/icons-material'

import { phoneNumber } from 'helpers/formatters'

import { color } from 'styles/colors'

import { screenSize } from 'styles/shared'

import Tooltip from 'components/Tooltip'
import PiiFlags from 'pages/shared/CustomerForm/PiiFlags'

type Props = {
  piiHomeContactFirstName: string | null
  piiHomeContactLastName: string | null
  piiHomeAddressLine1: string | null
  piiHomeAddressLine2: string | null
  piiHomeAddressCity: string | null
  piiHomeAddressState: string | null
  piiHomeAddressPostalCode: string | null
  piiHomeContactPhoneNumber: string | null
  piiHomeContactEmailAddress: string | null
  onClickCopy: () => void
  isNewAndUnacknowledged: boolean | null
  isUpdatedAndUnacknowledged: boolean | null
  isDuplicateAndUnacknowledged: boolean | null
  isUpdateAcknowledged: boolean
  piiHomeDuplicateNames: string | null
  piiAcknowledgedAt: string | null
  piiDuplicateAck: boolean | null
  orginalPiiAcknowledgedAt?: string | null
  onChangeCallback: (customerAttributes: object) => void
}

const CustomersEntryPanel = ({
  piiHomeContactFirstName,
  piiHomeContactLastName,
  piiHomeAddressLine1,
  piiHomeAddressLine2,
  piiHomeAddressCity,
  piiHomeAddressState,
  piiHomeAddressPostalCode,
  piiHomeContactPhoneNumber,
  piiHomeContactEmailAddress,
  onClickCopy,
  isNewAndUnacknowledged,
  isUpdatedAndUnacknowledged,
  isDuplicateAndUnacknowledged,
  isUpdateAcknowledged,
  piiHomeDuplicateNames,
  piiAcknowledgedAt,
  piiDuplicateAck,
  orginalPiiAcknowledgedAt,
  onChangeCallback
}: Props) => {
  const [displayCustomerEntry, setDisplayCustomerEntry] = useState(false)

  return (
    <S.CustomersEntry>
      <S.TitleContainer>
        <S.TitlewithFlag>
          <S.Label>Customer's Entry</S.Label>
          {isNewAndUnacknowledged && (
            <S.Flag>New</S.Flag>
          )}
          {isUpdatedAndUnacknowledged && (
            <S.Flag>Updated</S.Flag>
          )}
          {isDuplicateAndUnacknowledged && (
            <Tooltip title={`This might be a duplicate account. Also see ${piiHomeDuplicateNames}`}>
              <S.Flag>Duplicate</S.Flag>
            </Tooltip>
          )}
        </S.TitlewithFlag>
        <S.ExpandIcon aria-label="Toggle Customer Entry" $displayCustomerEntry={displayCustomerEntry} onClick={() => setDisplayCustomerEntry(prev => !prev)} />
      </S.TitleContainer>
      <PiiFlags
        isNewAndUnacknowledged={isNewAndUnacknowledged}
        isUpdatedAndUnacknowledged={isUpdatedAndUnacknowledged}
        isDuplicateAndUnacknowledged={isDuplicateAndUnacknowledged}
        isUpdateAcknowledged={isUpdateAcknowledged}
        piiHomeDuplicateNames={piiHomeDuplicateNames}
        piiAcknowledgedAt={piiAcknowledgedAt}
        piiDuplicateAck={piiDuplicateAck}
        orginalPiiAcknowledgedAt={orginalPiiAcknowledgedAt}
        onChangeCallback={onChangeCallback}
        displayCustomerEntry={displayCustomerEntry}
      />
      <S.CustomersEntryPanel $displayCustomerEntry={displayCustomerEntry}>
        <S.Grid>
          <S.Label>First Name
            <S.Value>{piiHomeContactFirstName}</S.Value>
          </S.Label>
          <S.Label>Last Name
            <S.Value>{piiHomeContactLastName}</S.Value>
          </S.Label>
          <S.Label>Address 1
            <S.Value>{piiHomeAddressLine1}</S.Value>
          </S.Label>
          <S.Label>Address 2
            <S.Value>{piiHomeAddressLine2}</S.Value>
          </S.Label>
          <S.Label>City
            <S.Value>{piiHomeAddressCity}</S.Value>
          </S.Label>
          <S.CityZip>
            <div>State<S.Value>{piiHomeAddressState}</S.Value></div>
            <S.Zip>Zip<S.Value>{piiHomeAddressPostalCode}</S.Value></S.Zip>
          </S.CityZip>
          <S.Label>Phone Number
            <S.Value>{phoneNumber(piiHomeContactPhoneNumber)}</S.Value>
          </S.Label>
          <S.Label>Email
            <S.Value>{piiHomeContactEmailAddress}</S.Value>
          </S.Label>
        </S.Grid>
        <S.ReplaceLink onClick={onClickCopy} >
          <S.ButtonText>Replace below</S.ButtonText>
          <S.ArrowBackIcon style={{ marginRight: 5 }} />
        </S.ReplaceLink>
      </S.CustomersEntryPanel>
    </S.CustomersEntry>
  )
}

export default CustomersEntryPanel

const S = {
  ExpandIcon: styled(ChevronLeft)<{ $displayCustomerEntry: boolean }>`
    transform:  ${props => props.$displayCustomerEntry ? 'rotate(270deg)' : 'rotate(180deg)'}; ;
    margin-left: 5px;
    font-size: 30px;
  `,
  CustomersEntry: styled.div`
    white-space: nowrap;
    width: 100%;
    min-width: 390px;
    max-width: 800px;
    margin: auto 0px;

    @media ${screenSize.small} {
      margin: 0;
      width: fit-content;
      min-width: 100%;
    }
  `,
  CustomersEntryPanel: styled.div<{ $displayCustomerEntry: boolean }>`
    flex-direction: column;
    overflow: hidden;
    padding-bottom: 10px;
    display: ${props => props.$displayCustomerEntry ? 'flex' : 'none'};
    transition: all 0.3s ease-in-out;
  `,
  TitleContainer: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 0px 10px;
    font-weight: bold;

    > h3 {
      margin: 5px;
    }

     @media ${screenSize.small} {
      padding: 5px 10px;
    }
  `,
  Grid: styled.div`
    display: grid;
    grid-template-columns: 40% 60%;
    grid-auto-rows: 60px;
    padding: 15px 15px 0;
    background-color: ${color('ancillaryInfoHeaderBackground')};
    border-radius: 15px;

    @media ${screenSize.small} {
      padding: 10px 10px 10px;
      grid-auto-rows: 55px;
      display: flex;
      flex-direction: column;
      gap: 15px;
    }
  `,
  Flag: styled.div`
    display: inline-block;
    font-weight: 600;
    font-size: 12px;
    border-radius: 5px;
    background: ${color('flagBackground')};
    color: ${color('flagText')};
    text-align: center;
    margin: 0 0 0 10px;
    padding: 5px;
  `,
  Label: styled.div`
    font-weight: lighter;
  `,
  Value: styled.div`
    font-weight: bold;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  `,
  CityZip: styled.div`
    display: -webkit-box;
  `,
  Zip: styled.div`
    padding-left: 70px;

     @media ${screenSize.small} {
      padding-left: 30px;
    }
  `,
  ButtonText: styled.span`
    color: ${color('primaryButton')};
    font-size: 12px;
    font-weight: 700;

  `,
  ArrowBackIcon: styled(ArrowBackIcon)`
    color: ${color('primaryButton')};
    font-size: 16px;
    transform: rotate(270deg);
  `,
  ButtonContainer: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
  `,
  ReplaceLink: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 15px 10px;
    cursor: pointer;
  `,
  TitlewithFlag: styled.div`
    display: flex;
    align-items: center;
  `
}
